(() => {
  if (customElements.get('miai-quick-search')) return;
  class QuickSearch extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || !this.dialog.showModal) return;
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.status = this.querySelector('[data-search-status]');
      this.start = this.querySelector('[data-search-start]');
      this.all = this.querySelector('[data-search-all]');
      this.clear = this.querySelector('[data-search-clear]');
      this.sequence = 0;
      this.events = new AbortController();
      const on = (node, event, fn, capture = false) => node.addEventListener(event, fn, {signal:this.events.signal,capture});
      let selector = this.dataset.trigger || '.header__icon--search';
      try { document.querySelector(selector); } catch { selector = '.header__icon--search'; }
      this.triggers = [...document.querySelectorAll(selector)];
      this.triggers.forEach(node => { node.classList.add('miai-search-trigger'); node.dataset.searchLabel = this.dataset.label; });
      on(document, 'click', event => {
        if (!(event.target instanceof Element)) return;
        const trigger = event.target.closest(selector);
        if (!trigger || this.contains(trigger)) return;
        event.preventDefault(); event.stopImmediatePropagation();
        this.opener = trigger;
        this.dialog.showModal();
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.input.focus();
        this.changed();
      }, true);
      on(this.querySelector('[data-search-close]'), 'click', () => this.dialog.close());
      on(this.dialog, 'click', event => { if (event.target === this.dialog) this.dialog.close(); });
      on(this.dialog, 'close', () => {
        this.cancel();
        document.body.style.overflow = this.previousOverflow || '';
        this.opener?.focus();
      });
      on(this.input, 'input', () => this.changed());
      on(this.clear, 'click', () => { this.input.value = ''; this.changed(); this.input.focus(); });
      on(this.querySelector('form'), 'submit', event => {
        if (!this.input.value.trim()) { event.preventDefault(); this.input.focus(); }
      });
      on(this.input, 'keydown', event => {
        if (event.key === 'ArrowDown') {
          const link = this.results.querySelector('a');
          if (link) { event.preventDefault(); link.focus(); }
        }
      });
      on(this.results, 'keydown', event => {
        if (!['ArrowDown','ArrowUp'].includes(event.key)) return;
        const links = [...this.results.querySelectorAll('a')];
        const index = links.indexOf(document.activeElement);
        event.preventDefault();
        if (event.key === 'ArrowUp' && index <= 0) this.input.focus();
        else links[Math.min(links.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1))]?.focus();
      });
    }
    cancel() {
      clearTimeout(this.timer);
      clearTimeout(this.deadline);
      this.request?.abort();
      this.sequence++;
    }
    changed() {
      this.cancel();
      const q = this.input.value.trim();
      this.clear.hidden = !q;
      this.start.hidden = !!q;
      this.results.replaceChildren();
      this.results.removeAttribute('aria-busy');
      this.status.textContent = '';
      this.all.hidden = !q;
      const url = new URL(this.querySelector('form').action);
      url.searchParams.set('q', q); url.searchParams.set('options[prefix]', 'last');
      this.all.href = url.href;
      if (!q) return;
      this.status.textContent = this.dataset.loading;
      const seq = this.sequence;
      this.timer = setTimeout(() => this.search(q, seq), 220);
    }
    async search(q, seq) {
      this.request = new AbortController();
      const controller = this.request;
      this.deadline = setTimeout(() => controller.abort(), 6500);
      this.results.setAttribute('aria-busy', 'true');
      const url = new URL(this.dataset.endpoint, location.origin);
      url.searchParams.set('q', q);
      url.searchParams.set('resources[type]', 'product,collection,article');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      try {
        const response = await fetch(url, {signal:controller.signal, headers:{Accept:'application/json'}});
        if (!response.ok) throw new Error('unavailable');
        const payload = await response.json();
        if (seq !== this.sequence || !this.dialog.open) return;
        const rows = payload.resources?.results;
        if (!rows) throw new Error('invalid');
        this.render(rows);
      } catch {
        if (seq === this.sequence && this.dialog.open) this.status.textContent = this.dataset.error;
      } finally {
        if (seq === this.sequence) {
          clearTimeout(this.deadline);
          this.results.removeAttribute('aria-busy');
        }
      }
    }
    safeUrl(value, image = false) {
      if (!value || typeof value !== 'string') return '';
      try {
        const url = new URL(value, location.origin);
        if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
        if (!image && url.origin !== location.origin) return '';
        if (image && url.origin !== location.origin && url.hostname !== 'cdn.shopify.com' && !url.hostname.endsWith('.myshopify.com')) return '';
        return url.href;
      } catch { return ''; }
    }
    render(rows) {
      let count = 0;
      for (const [key,label] of [['products',this.dataset.products],['collections',this.dataset.collections],['articles',this.dataset.articles]]) {
        const items = Array.isArray(rows[key]) ? rows[key].slice(0,key === 'products' ? 6 : 3) : [];
        if (!items.length) continue;
        const group = document.createElement('section');
        group.className = 'mqs-group mqs-' + key;
        const title = document.createElement('h3'); title.textContent = label; group.append(title);
        const list = document.createElement('ul');
        for (const item of items) {
          const href = this.safeUrl(item.url);
          if (!href) continue;
          const li = document.createElement('li'), link = document.createElement('a');
          link.href = href;
          if (key === 'products') {
            const source = this.safeUrl(item.featured_image?.url || item.image || '', true);
            if (source) {
              const img = document.createElement('img');
              const imageUrl = new URL(source); imageUrl.searchParams.set('width','160');
              img.src = imageUrl.href; img.alt = ''; img.width = 72; img.height = 72; img.loading = 'lazy';
              link.append(img);
            }
          }
          const copy = document.createElement('span'), name = document.createElement('span');
          name.className = 'mqs-name'; name.textContent = item.title; copy.append(name);
          if (key === 'products') {
            const price = document.createElement('span'); price.className = 'mqs-price';
            const value = Number(item.price);
            if (item.price != null && Number.isFinite(value)) {
              try { price.textContent = new Intl.NumberFormat(this.dataset.locale || 'en', {style:'currency',currency:this.dataset.currency}).format(value); } catch {}
            }
            if (item.available === false) price.textContent += ' · ' + this.dataset.soldout;
            copy.append(price);
          }
          link.append(copy); li.append(link); list.append(li); count++;
        }
        group.append(list); this.results.append(group);
      }
      this.status.textContent = count ? this.dataset.found.replace('[count]',String(count)) : this.dataset.empty;
    }
    disconnectedCallback() {
      this.cancel(); this.events?.abort();
      if (this.dialog?.open) { this.dialog.close(); document.body.style.overflow = this.previousOverflow || ''; }
      this.triggers?.forEach(node => {node.classList.remove('miai-search-trigger'); delete node.dataset.searchLabel;});
      this.ready = false;
    }
  }
  customElements.define('miai-quick-search', QuickSearch);
})();